import { FormikProps, useFormikContext } from 'formik'
import {
  PrivateOffer,
  ScheduleType,
} from '../../../../modules/privateOffer/types'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo } from 'react'
import { BoxV4 } from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './AmiPrivateOfferInstallmentsSection.styles'
import { getAmiInstallmentsBasedOnSchedule } from '../../../../utils/amiPrivateOfferUtil'
import { InstallmentsSection } from '../../../PrivateOfferComponents/InstallmentsSection/InstallmentsSection'
import { TotalContractValueSection } from '../../../../common/components/TotalContractValueSection/TotalContractValueSection'

export const calculateDurationInMonths = (
  containerOfferServiceLength: string | number | undefined
): number => {
  if (!containerOfferServiceLength) {
    return 1
  }

  let durationInMonths: number

  // If serviceLength is a number, assume it's already in months
  if (typeof containerOfferServiceLength === 'number') {
    durationInMonths = containerOfferServiceLength
  } else {
    // Parse string format like "12M", "1Y", "30D", "4W"
    const match = containerOfferServiceLength
      .toString()
      .match(/^(\d+(?:\.\d+)?)([MYWD])?$/i)

    if (!match) {
      // If format doesn't match, try to parse as a plain number
      const numericValue = parseFloat(containerOfferServiceLength)
      if (!isNaN(numericValue) && numericValue > 0) {
        durationInMonths = numericValue
      } else {
        return 1
      }
    } else {
      const value = parseFloat(match[1])
      const unit = match[2]?.toUpperCase()

      switch (unit) {
        case 'Y':
          durationInMonths = value * 12
          break
        case 'M':
          durationInMonths = value
          break
        case 'W':
          durationInMonths = value / 4.345 // average weeks per month
          break
        case 'D':
          durationInMonths = value / 30.44 // average days per month
          break
        default:
          // No unit specified, assume months
          durationInMonths = value
      }
    }
  }

  // If calculated value is less than 1 month, return 1
  return durationInMonths < 1 ? 1 : Math.round(durationInMonths * 100) / 100
}

export const AmiPrivateOfferInstallmentsSection = () => {
  const formikProps = useFormikContext() as FormikProps<PrivateOffer>
  const { values, touched, errors, setFieldValue, handleBlur } = formikProps
  const { classes } = useStyles()

  const regenerateInstallments = useCallback(
    (
      tcv: number,
      containerOfferServiceLength: string | number | undefined,
      flexiblePaymentType: ScheduleType | undefined,
      offerStartDate: string | undefined,
      setFieldValueFn: (field: string, value: any) => Promise<any>
    ) => {
      const durationInMonths = calculateDurationInMonths(
        containerOfferServiceLength
      )
      const paymentSchedule = flexiblePaymentType || ScheduleType.MONTHLY
      const effectiveOfferStartDate =
        offerStartDate || new Date().toISOString().split('T')[0]

      // Only regenerate if not CUSTOM
      if (paymentSchedule !== ScheduleType.CUSTOM && durationInMonths) {
        const newInstallments = getAmiInstallmentsBasedOnSchedule({
          tcv: tcv,
          durationInMonths: durationInMonths,
          paymentSchedule: paymentSchedule as ScheduleType,
          now: effectiveOfferStartDate,
        })
        setFieldValueFn('installments', newInstallments)
      }
    },
    [calculateDurationInMonths, getAmiInstallmentsBasedOnSchedule]
  )

  const debouncedRegenerateInstallments = useMemo(
    () => debounce(regenerateInstallments, 500),
    [regenerateInstallments]
  )

  useEffect(() => {
    return () => {
      debouncedRegenerateInstallments.cancel()
    }
  }, [debouncedRegenerateInstallments])

  const handleTcvChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value
      const numericValue = value !== '' ? Number(value) : undefined
      await setFieldValue('tcv', numericValue)
      // Regenerate installments if containerOfferServiceLength is already set (debounced)
      const serviceLength = values.containerOfferServiceLength || ''
      if (numericValue && serviceLength) {
        debouncedRegenerateInstallments(
          numericValue,
          serviceLength,
          values.flexiblePaymentType,
          values.offerStartDate,
          setFieldValue
        )
      }
    },
    [
      setFieldValue,
      values.containerOfferServiceLength,
      values.flexiblePaymentType,
      values.offerStartDate,
      debouncedRegenerateInstallments,
    ]
  )

  const durationInMonths = useMemo(
    () => calculateDurationInMonths(values.containerOfferServiceLength || ''),
    [values.containerOfferServiceLength]
  )

  return (
    <div>
      <BoxV4
        heading={'Installments'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        <div className={classes.sectionContainer}>
          {values?.flexiblePaymentType !== ScheduleType.CUSTOM && (
            <TotalContractValueSection
              tcvValue={values?.tcv}
              firstInstallmentDateValue={values?.firstInstallmentDate}
              touched={{
                tcv: touched.tcv,
                firstInstallmentDate: touched.firstInstallmentDate,
              }}
              errors={{
                tcv: errors.tcv as string | undefined,
                firstInstallmentDate: errors.firstInstallmentDate as
                  | string
                  | undefined,
              }}
              onTcvChange={handleTcvChange}
              onFirstInstallmentDateChange={async date => {
                await setFieldValue('firstInstallmentDate', date)

                // Regenerate all installments with the new first installment date
                const serviceLength = values.containerOfferServiceLength || ''
                const tcv = values.tcv
                const paymentSchedule =
                  values.flexiblePaymentType || ScheduleType.MONTHLY

                if (
                  paymentSchedule !== ScheduleType.CUSTOM &&
                  tcv &&
                  serviceLength &&
                  date
                ) {
                  const durationInMonths =
                    calculateDurationInMonths(serviceLength)
                  const newInstallments = getAmiInstallmentsBasedOnSchedule({
                    tcv: tcv,
                    durationInMonths: durationInMonths,
                    paymentSchedule: paymentSchedule as ScheduleType,
                    now: date,
                  })
                  await setFieldValue('installments', newInstallments)
                } else if (
                  values?.installments &&
                  values?.installments?.length > 0
                ) {
                  // Fallback: only update first installment if we can't regenerate
                  await setFieldValue('installments[0].paymentDate', date)
                }
              }}
              onBlur={handleBlur}
              validate={true}
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
            />
          )}
          <InstallmentsSection
            installments={values?.installments}
            tcv={values?.tcv}
            durationInMonths={durationInMonths}
            firstInstallmentDate={values?.firstInstallmentDate}
            handleChangeInstallmentsAmount={async updatedInstallments => {
              await setFieldValue('installments', updatedInstallments)
            }}
            validate={true}
          />
        </div>
      </BoxV4>
    </div>
  )
}
